import {
  annotationsActions,
  annotationsSelectors,
  applicationActions,
  applicationSelectors,
  authSelectors,
  bookSelectors,
  cloudSelectors,
  readerSelectors,
  RootState,
  usersSelectors,
} from '@readcloud/state';
import React from 'react';
import { useSelector } from 'react-redux';
import { ShapesRenderer } from './ShapesRenderer/ShapesRenderer';

import { Annotation } from '@readcloud/data';
import { useAppDispatch } from '@readcloud/state';
import { filterAnnoByOptions } from '../../utils/annotationUtils';
import { canEditAnnotation, isPublisherOfBook } from '../../constants';
import { useDeepCompareMemoize } from '../../hooks/useDeepCompareMemoize';

export const ConnectedShapesRenderer = (props: {
  pageNumber: number;
  height: number;
  width: number;
  scale: number;
  outerContainer: HTMLDivElement;
  pagesContainer: HTMLDivElement;
  pageContainer: HTMLDivElement;
}) => {
  const readerData = useSelector(readerSelectors.getReaderData);
  const appData = useSelector(applicationSelectors.getApplcationState);
  const userPrefs = useSelector(applicationSelectors.getUserPrefs);
  const user = useSelector(authSelectors.getUser);
  const userScopeParams = useSelector(authSelectors.getUserScopeParams);
  const knownUsers = useSelector(usersSelectors.getKnownUsers);
  const knownClouds = useSelector(cloudSelectors.getKnownClouds);
  const documentControl = useSelector(readerSelectors.getDocumentControl);
  const selectedAnnotation = useSelector(
    annotationsSelectors.getSelectedAnnotation
  );
  const currentTool = useSelector(annotationsSelectors.getCurrentTool);
  const currentlyDrawing = useSelector(
    annotationsSelectors.getCurrentlyDrawing
  );
  const annotationByPage = useSelector((state: RootState) =>
    annotationsSelectors.getAnnotationByBookAndPage(state, props.pageNumber)
  );
  const annotationFilters = useSelector(
    annotationsSelectors.getAnnotationFilters
  );
  const book = useSelector((state: RootState) =>
    bookSelectors.getBook(state, readerData.bookId)
  );
  const dispatch = useAppDispatch();

  const canEditAnno = (anno: Annotation) => {
    return canEditAnnotation(user, anno);
  };

  const filteredAnnotationsByPage = React.useMemo(() => {
    return annotationFilters
      ? annotationByPage.filter((anno) =>
          filterAnnoByOptions(
            anno,
            annotationFilters.types,
            annotationFilters.users
          )
        )
      : annotationByPage;
  }, [
    useDeepCompareMemoize(annotationByPage),
    useDeepCompareMemoize(annotationFilters),
  ]);

  return user && readerData.bookId ? (
    <ShapesRenderer
      outerContainer={props.outerContainer}
      pagesContainer={props.pagesContainer}
      pageContainer={props.pageContainer}
      canEditPublisherOptions={() => isPublisherOfBook(book, userScopeParams)}
      disableDrawing={
        !(user?.permissions?.indexOf('Drawing') !== -1) &&
        !(user?.permissions?.indexOf('Media') !== -1)
      }
      disableOverlay={
        !(user?.permissions?.indexOf('Drawing') !== -1) &&
        !(user?.permissions?.indexOf('Media') !== -1) &&
        !(user?.permissions?.indexOf('HTML') !== -1) &&
        !(user?.permissions?.indexOf('Experimental') !== -1)
      }
      canEditAnnotation={canEditAnno}
      annotations={filteredAnnotationsByPage}
      selectedAnnotation={selectedAnnotation?.id}
      onSelectedAnnotationChange={(id) =>
        dispatch(annotationsActions.setSelectedAnnotation(id))
      }
      knownClouds={knownClouds}
      knownUsers={knownUsers}
      currentlyDrawing={!!currentlyDrawing}
      currentTool={currentTool}
      handleAdd={(anno) =>
        dispatch(annotationsActions.asyncActions.addAnnotation(anno)).unwrap()
      }
      handleUpdate={(anno) =>
        dispatch(
          annotationsActions.asyncActions.updateAnnotation(anno)
        ).unwrap()
      }
      handleDelete={(id) =>
        dispatch(annotationsActions.asyncActions.deleteAnnotation(id)).unwrap()
      }
      handleToolChange={(tool) =>
        dispatch(annotationsActions.updateCurrentTool(tool))
      }
      userId={user.id}
      bookId={readerData.bookId}
      height={props.height}
      width={props.width}
      scale={props.scale}
      pageNumber={props.pageNumber}
      isPublisher={isPublisherOfBook(book, userScopeParams)}
      updateAnnoPreferences={(type, data) =>
        dispatch(
          applicationActions.asyncActions.updateAnnoPrefs({ type, data })
        )
      }
      annoPrefs={userPrefs?.annoPrefs}
    />
  ) : null;
};
